import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import * as Highcharts from 'highcharts';
import {Options} from 'highcharts';
import {InsightsService} from "../../services/insights/insights.service";
import {AlertBannerService, AlertType} from "../../lib/alert-banner";
import {TrendChartService} from "../../services/trend-chart/trend-chart.service";
import moment from "moment";

@Component({
  selector: 'app-pulse-participation-trend',
  templateUrl: './pulse-participation-trend.component.pug',
  styleUrls: ['./pulse-participation-trend.component.scss'],
  standalone: false
})
export class PulseParticipationTrendComponent implements OnInit, OnChanges {

  @Input() start!: any;
  @Input() end!: any;
  @Input() questionnaire!: any;
  @Input() request: any;
  @Input() survey: any;
  @Input() department: any;
  @Input() filterBy!: any;
  @Input() state!: string;
  @Input() data: any = { result: [], result_type: '', start: '', end: '' };
  @Output() dataLoaded = new EventEmitter<void>();
  updateFlag = false;
  tickPositions: any = [];

  dateFormat: any = new Map([
    ["dates", "YYYY-MM-DD"],
    ["weeks", "YYYY_W"],
    ["months", "YYYY_MM"],
    ["quarters", "YYYY_Q"],
    ["years", "YYYY"],
  ]);

  Highcharts: typeof Highcharts = Highcharts;
  title: Highcharts.TitleOptions = {
    text: `-`,
    align: 'left',
    style: {"fontFamily": "TT Commons", fontWeight: 'normal', fontSize: "18px"}
  }

  subtitle: Highcharts.SubtitleOptions = {
    text: $localize`Displaying 0 requests`,
    align: 'left',
    style: {"fontFamily": "TT Commons"}
  }

  tooltip: Highcharts.TooltipOptions = {
    enabled: true,
    backgroundColor: 'white',
    borderWidth: '0',
    padding: 12,
    distance: 16,
    borderRadius: 4,
    shadow: true,
    useHTML: true,
    outside: true,
    formatter: function() {
      const tooltipFormat: any = new Map([
        ["dates", "D MMM YYYY"],
        ["weeks", "[Week starting] D MMM YYYY"],
        ["months", "MMMM YYYY"],
        ["quarters", "[Q]Q, YYYY"],
        ["years", "YYYY"],
      ]);

      // When doing code cleanup, reminder to remove line below and ffENT5059: this.featureFlagEnt5059 inside series.push
      return  `
      <div class='participation-tooltip' style="width:140px">
        <span class='tooltip-heading'>${moment.unix(this.x as number / 1000).format(tooltipFormat.get(this.point.custom.result_type))}</span>
        <div class='tooltip-point'>
          <div class='tooltip-row'><b style='color:#32735F'>${this.point.custom.completed}</b> Completed</div>
          <div class='tooltip-row'><b style='color:#000000'>${this.point.custom.stopped}</b> Stopped</div>
        </div>
      </div> `;
    },
    style: {
      fontFamily: 'TT Commons',
      fontWeight: '500',
      fontSize: '14px',
      padding: '0px',
      width: 135
    }
  }

  legend: Highcharts.LegendOptions = {
    enabled: false,
  }

  series: any = [
    {
      type: "column",
      pointWidth: 12,
      borderRadiusTopLeft: 2,
      borderRadiusTopRight: 2,
    }
  ]

  chartOptions: Options = TrendChartService.getOptions(
    [], 0, 100, 300, 800, '#FFF', 'rgb(50,115,95)', 'rgb(255, 255, 255)', ['#32735F', '#EAF1EF'], 'months', this.title, this.subtitle, this.tooltip, this.legend, this.series);

  constructor(private insightsService: InsightsService,
              private alertList: AlertBannerService) {
  }

  ngOnInit(): void {
    this.tooltip.outside = false;
  }

  get totalRequests() {
    const items = this.data["result"].filter(item => item.participation !== 0);
    return items.reduce((a, b) => { return a + b.completed + b.stopped + b.in_progress }, 0);
  }

  loadData(): void {
    this.insightsService.getParticipationTrend(this.start, this.end, this.survey, this.filterBy, this.state, 'pulse', this.request, this.department).subscribe(data => {
      this.data = data;
      let result_type = this.dateFormat.get(data['result_type']);
      let series: any = [];
      let maxParticipation = -Infinity;
      let lastPoint = -Infinity;
      this.tickPositions = [];
      const rangeMs = this.end.diff(this.start);
      const min_diff = rangeMs / 7;
      let result: any[] = [];

      result = data.result.filter((item: any) => item.participation !== 0).sort((a: any, b: any) => {
        const [yearA, monthA] = a.date.split('_').map(Number);
        const [yearB, monthB] = b.date.split('_').map(Number);
        return yearA - yearB || monthA - monthB;
      });

      for (let i = 0; i < result.length; i++) {
        let d = result[i];
        let date = moment.utc(d['date'], result_type).toDate().getTime();
        let currentPoint = date;
        let diff = currentPoint - lastPoint;
        if ((i == 0 || i == result.length - 1 || (diff > min_diff))) {
          lastPoint = currentPoint;
          this.tickPositions.push(currentPoint);
        }
        series.push({ x: date, y: d['participation'],
          className: d['in_progress'] > 0 ? 'in-progress' : '',
          custom: {
              completed: d['completed'] ? d['completed'] : 0,
              stopped: d['stopped'] ? d['stopped'] : 0,
              in_progress: d['in_progress'] ? d['in_progress'] : 0,
              result_type: data['result_type'] ? data['result_type'] : 'dates',
          }});
        }

      if (series.length > 0) {
        const resultType = data['result_type'] || 'months';
        const { series: newSeries, tickPositions: newTicks } = this.addZeroLabelsWhereNoData(series, min_diff, resultType);
        series = newSeries;
        this.tickPositions = newTicks;
      }

      let label_format = '';
      if (data['result_type'] == 'years') {
        label_format = 'YYYY';
      } else if (data['result_type'] == 'quarters') {
        label_format = '[Q]Q, YYYY';
      } else if (data['result_type'] == 'months') {
        label_format = 'MMMM YYYY';
      } else if (data['result_type'] == 'dates' || data['result_type'] == 'weeks') {
        label_format = 'DD MMM YYYY';
      }
      else {
        label_format = 'L';
      }
      this.chartOptions.title = { text: `${moment(this.start).format(label_format)} - ${moment(this.end).format(label_format)}` }

        let requestCount = this.totalRequests;
        if (requestCount === 1) {
          this.chartOptions.subtitle = {text: $localize`Displaying ${requestCount} request`};
        } else {
          this.chartOptions.subtitle = {text: $localize`Displaying ${requestCount} requests`};
        }
      this.chartOptions.xAxis = {'tickPositions': this.tickPositions}
      let trend_chart_result_type = data['result_type'];
      if (data['result_type'] == 'weeks') { trend_chart_result_type = 'pulse_weeks'; }
      if (data['result_type'] == 'months') { trend_chart_result_type = 'pulse_months'; }
      TrendChartService.setChartOptionsFromData(this.chartOptions, series, trend_chart_result_type);
      const resultType = data['result_type'];
      this.chartOptions.xAxis = {
        type: 'datetime',
        tickPositions: this.tickPositions,
        labels: {
          rotation: 0,
          align: 'center',
          style: {
            fontSize: '12px',
            fontFamily: 'TT Commons'
          },
          formatter: function () {
            let line1 = '';
            let line2 = '';

            if (resultType === 'years') {
              return moment(this.value).format('YYYY');
            } else if (resultType === 'quarters') {
              line1 = moment(this.value).format('[Q]Q');
              line2 = moment(this.value).format('YYYY');
            } else if (resultType === 'months') {
              line1 = moment(this.value).format('MMM');
              line2 = moment(this.value).format('YYYY');
            } else {
              line1 = moment(this.value).format('DD MMM');
              line2 = moment(this.value).format('YYYY');
            }

            return `${line1}<br><div style="text-align:center">${line2}</div>`;
          },
          useHTML: true
        },
      };


      this.updateFlag = true;
      this.emitDataLoaded();
    }, error => {
      console.log(error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
      TrendChartService.setChartOptionsFromData(this.chartOptions, [], 'years');
      this.updateFlag = true;
      this.emitDataLoaded();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadData();
  }

  private emitDataLoaded(): void {
    this.dataLoaded.emit();
  }

  /**
   * Where there is no data between two points, add labels (ticks) and series points with y: 0.
   * For period types, add a label at every period start in the gap that passes the overlap check.
   * For dates, add at most one at the numeric mid.
   */
  private addZeroLabelsWhereNoData(series: any[], minDiffMs: number, resultType: string): { series: any[]; tickPositions: number[] } {
    const result_type = resultType || 'dates';
    const unit = resultType === 'years' ? 'year' : resultType === 'quarters' ? 'quarter' : resultType === 'months' ? 'month' : resultType === 'weeks' ? 'isoWeek' : null;
    const tickPositions: number[] = [series[0].x];
    const zeroPoints: { x: number; afterIndex: number }[] = [];
    const minSpacingForGap = minDiffMs / 2;

    for (let i = 0; i < series.length - 1; i++) {
      const a = series[i].x;
      const b = series[i + 1].x;

      if (unit) { // period types
        let cursor = unit === 'isoWeek'
          ? moment.utc(a).startOf('isoWeek').add(1, 'week').valueOf()
          : moment.utc(a).add(1, unit).startOf(unit as moment.unitOfTime.StartOf).valueOf();
        while (cursor < b) {
          const lastTick = tickPositions[tickPositions.length - 1];
          if (cursor - lastTick >= minSpacingForGap) {
            tickPositions.push(cursor);
            zeroPoints.push({ x: cursor, afterIndex: i });
          }
          cursor = unit === 'isoWeek'
            ? moment.utc(cursor).add(1, 'week').valueOf()
            : moment.utc(cursor).add(1, unit).startOf(unit as moment.unitOfTime.StartOf).valueOf();
        }
      } else { // dates
        const gapPos = Math.round((a + b) / 2);
        const hasGap = gapPos > a && gapPos < b;
        const lastTick = tickPositions[tickPositions.length - 1];
        if (hasGap && gapPos - lastTick >= minDiffMs) {
          tickPositions.push(gapPos);
          zeroPoints.push({ x: gapPos, afterIndex: i });
        }
      }
      tickPositions.push(b);
    }

    //sort the array by date 
    const newSeries: any[] = [];
    for (let i = 0; i < series.length; i++) {
      newSeries.push(series[i]);
      zeroPoints.filter(z => z.afterIndex === i).forEach(z => {
        newSeries.push({
          x: z.x,
          y: 0,
          className: '',
          custom: { completed: 0, stopped: 0, in_progress: 0, result_type: result_type }
        });
      });
    }
    newSeries.sort((p, q) => p.x - q.x);
    return { series: newSeries, tickPositions };
  }
}
